<template>
    <div class="form-group">
        <div class="d-flex align-items-center">
            <label :for="id">
                {{ label }}
            </label>
            <div v-if="info" class="with-tooltip">
                <img class="info-tooltip"
                     src="../../assets/img/question.svg">
                <div class="tooltiptext">
                    {{ info }}
                </div>
            </div>
        </div>
        <div class="input-wrapper" v-click-outside="close">
            <input ref="input"
                   type="text"
                   readonly
                   placeholder="__:__ AM"
                   :id="id"
                   :name="name"
                   :style="styleInput"
                   v-model="inputValue"
                   v-validate="{required}"
                   @click="toggle"
                   @change="onChange">
            <img src="../../assets/img/clock.svg"
                 @click="toggle">
            <div v-if="isOpened"
                 ref="popup"
                 :style="popupStyle"
                 :class="['date-picker', mobilePosition ? 'left' : 'right']">
                <div v-if="showTriangle"
                     ref="arrow"
                     :class="['arrow-up', mobilePosition ? 'left-arrow-mobile' : 'right-arrow-mobile']"
                     :style="{ ...triangleStyle, ...arrowStyle}">
                </div>
                <div class="row justify-content-center">
                    <div class="time-select">
                        <div class="wrapper"
                             @click="nextHour">
                            <i class="material-icons">
                                keyboard_arrow_up
                            </i>
                        </div>
                        <input type="text"
                               maxlength="2"
                               :value="hours"
                               class="number wrapper"
                               @change="onHourChange">
                        <div class="wrapper"
                             @click="previousHour">
                            <i class="material-icons">
                                keyboard_arrow_down
                            </i>
                        </div>
                    </div>
                    <div class="delimiter">
                        :
                    </div>
                    <div class="time-select">
                        <div class="wrapper"
                             @click="nextMinute">
                            <i class="material-icons">
                                keyboard_arrow_up
                            </i>
                        </div>
                        <input type="text"
                               maxlength="2"
                               :value="minutes"
                               class="number wrapper"
                               @change="onMinuteChange">
                        <div class="wrapper"
                             @click="previousMinute">
                            <i class="material-icons">
                                keyboard_arrow_down
                            </i>
                        </div>
                    </div>
                    <div class="format">
                        <div class="wrapper"
                             @click="toggleTT">
                            {{ dayTime }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="error">{{ errors.first(name) }}</div>
        </div>
    </div>
</template>

<script>
    import Inputmask from 'inputmask';
    import dateFormat from 'dateformat';
    import ClickOutside from 'vue-click-outside';
    import { mapGetters } from 'vuex';
    import { DatePopupMixin } from '../../mixins/DatePopupMixin';

    export default {
        name: 'TimePicker',
        directives: {ClickOutside},
        mixins: [DatePopupMixin()],
        props: {
            label: String,
            id: {
                type: String,
                required: true
            },
            value: [String, Date],
            info: String,
            popupStyle: {
                type: Object
            },
            showTriangle: {
                type: Boolean,
                default: true
            },
            styleInput: {
                type: Object,
                default: () => ({})
            },
            defaultHours: {
                type: [Number, String]
            },
            defaultMinutes: {
                type: [Number, String]
            },
            defaultTT: {
                type: String
            },
            triangleStyle: {
                type: Object,
                default: () => ({})
            },
            required: {
                type: Boolean,
                default: true
            },
        },
        inject: ['$validator'],
        $_veeValidate: {
            name() {
                return this.name;
            },
            value() {
                return this.value;
            }
        },
        data() {
            return {
                date: new Date(),
                isOpened: false,
                inputValue: '',
                name: 'time-picker-' + Math.random() * 10,
                mobilePosition: false
            }
        },
        computed: {
            ...mapGetters(['isMobile']),

            hours() {
                return dateFormat(this.date, 'hh');
            },
            minutes() {
                return dateFormat(this.date, 'MM');
            },
            dayTime() {
                return dateFormat(this.date, 'TT');
            }
        },
        watch: {
            date(value) {
                value && this.setDate(value);
            },
            value: {
                handler(value) {
                    if (value) {
                        const timeParts = value.split(':');
                        const hours = timeParts[0];
                        const minutes = timeParts[1];
                        const date = new Date(this.date);
                        date.setHours(hours);
                        date.setMinutes(minutes);
                        this.date = new Date(date);
                        this.$emit('input', dateFormat(this.date, 'HH:MM'));
                    }
                },
                immediate: true
            },
            defaultHours(value) {
                if (value !== undefined && !this.inputValue) {
                    const date = new Date(this.date);
                    date.setHours(value);
                    this.setDate(date);
                }
            },
            defaultMinutes(value) {
                if (value && !this.inputValue) {
                    const date = new Date(this.date);
                    date.setMinutes(value);
                    this.setDate(date);
                }
            }
        },
        methods: {
            addMask() {
                const input = this.$refs.input;
                Inputmask({
                    showMaskOnHover: true,
                    regex: '[0-1][0-9]:[0-5][0-9] [AP]M'
                }).mask(input);
            },
            nextHour() {
                const date = new Date(this.date);
                date.setHours(this.date.getHours() + 1);
                this.date = date;
            },
            previousHour() {
                const date = new Date(this.date);
                date.setHours(this.date.getHours() - 1);
                this.date = date;
            },
            nextMinute() {
                const date = new Date(this.date);
                date.setMinutes(this.date.getMinutes() + 1);
                this.date = date;
            },
            previousMinute() {
                const date = new Date(this.date);
                date.setMinutes(this.date.getMinutes() - 1);
                this.date = date;
            },
            toggleTT() {
                const date = new Date(this.date);
                if (this.dayTime === 'AM') {
                    date.setHours(date.getHours() + 12);
                }
                else {
                    date.setHours(date.getHours() - 12);
                }
                this.date = date;
            },
            toggle(e) {
                this.mobilePosition = window.innerWidth/2 > e.clientX;
                this.isOpened = !this.isOpened;
            },
            close() {
                this.isOpened = false;
            },
            onChange() {
                this.$emit('change', dateFormat(this.date, 'HH:MM'));
            },
            setDate(date) {
                const value = dateFormat(date, 'hh:MM TT');
                this.inputValue = value;
                this.$emit('input', dateFormat(date, 'HH:MM'));
            },
            onHourChange(event) {
                const date = new Date(this.date);
                if (this.dayTime === 'AM') {
                    date.setHours(event.target.value);
                } else {
                    date.setHours(event.target.value);
                    if (Number(event.target.value) < 12) {
                        date.setHours(date.getHours() - 12)
                    }
                }

                this.date = date;
            },
            onMinuteChange(event) {
                const date = new Date(this.date);
                date.setMinutes(event.target.value);
                this.date = date;
            }
        },
        mounted() {
            this.addMask();
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../assets/colors";

    $paddingTop: 20px;
   .form-group {
       width: 100%;
       .input-wrapper {
           position: relative;
           color: $Dark;
           width: 100%;
           input::placeholder {
               color: $Dark;
           }
           img {
               cursor: pointer;
               position: absolute;
               right: 15px;
               top: 15px;
               z-index: 2;
               @media screen and (max-width: 425px) {
                   right: 5px;
                   top: 15px;
               }
           }
           .date-picker {
               z-index: 8;
               padding: 20px 40px;
               position: absolute;
               top: 60px;
               background: $White;
               width: 320px;
               box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.25);
               @media screen and (max-width: 480px) {
                   padding: 20px;
                   width: 285px;
                   right: -10px !important;
               }
               @media screen and (max-width: 425px) {
                   right: -14px !important;
               }

               .time-select {
                   display: flex;
                   flex-direction: column;
                   .number {
                       cursor: auto;
                       margin: 40px 0;
                       text-align: center;
                       font-size: 20px;
                       &:hover {
                           background: $White;
                       }
                   }
               }
               .wrapper {
                   width: 60px;
                   height: 50px;
                   display: flex;
                   justify-content: center;
                   align-items: center;
                   cursor: pointer;
                   &:hover {
                       background: $ControlsGrey;
                   }
                   i {
                       font-size: 45px;
                   }
               }
               .delimiter {
                   display: flex;
                   justify-content: center;
                   align-items: center;
                   margin: 0 20px;
               }
               .format {
                   display: flex;
                   justify-content: center;
                   align-items: center;
                   margin: 0 0 0 25px;
                   .wrapper {
                       background: $SoftBlue;
                       color: $White;
                       border-radius: 2px;
                   }
               }
           }
       }
   }

    $size: 6px;
    .arrow-up {
        position: relative;
        left: calc(100% + 10px);
        top: -($paddingTop + $size);
        width: 0;
        height: 0;
        border-left: $size solid transparent;
        border-right: $size solid transparent;
        border-bottom: $size solid $White;
        box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.25);
        z-index: 2;
    }

    .right-arrow-mobile {
        left: 98%;
    }

    .left-arrow-mobile {
        left: 0;
    }

    .right {
        right: 0;
        @media screen and (max-width: 450px) {
            right: -17px;
        }
    }

    .left {
        left: 0;
        @media screen and (max-width: 450px) {
            left: -17px;
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/controls/TimePicker.vue